import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Building, Save, Users, Calendar, Settings as SettingsIcon,
  CheckCircle, AlertCircle, Bell, Shield, Database, Clock,
  Mail, Globe, Palette, Lock, RefreshCw, Upload, BarChart3,
  FileText, CreditCard, Zap, Info, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const CompanySettings = () => {
  const { user } = useAuth();
  const { languageConfig } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Company Profile
  const [companyData, setCompanyData] = useState({
    companyName: '',
    logoUrl: '',
    industry: 'retail',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    fiscalYearStart: 'April'
  });
  
  // Company Settings from API
  const [settings, setSettings] = useState(null);
  
  // Notification Settings
  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    dailyDigest: true,
    criticalAlertsOnly: false,
    agentCompletionNotify: true,
    uploadNotifications: true,
    weeklyReportEmail: true
  });
  
  // Data Settings
  const [dataSettings, setDataSettings] = useState({
    dataRetentionDays: 365,
    autoArchive: true,
    anonymizeOldData: false,
    enableDataExport: true
  });
  
  // V28: Storage metrics
  const [storageMetrics, setStorageMetrics] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  
  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: 60,
    enforcePasswordPolicy: true,
    twoFactorEnabled: false,
    ipWhitelist: ''
  });

  const loadStorageMetrics = async () => {
    setStorageLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/company/storage`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStorageMetrics(response.data);
    } catch (error) {
      console.error('Failed to load storage metrics:', error);
    } finally {
      setStorageLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadStorageMetrics();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/company/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSettings(response.data);
      setCompanyData(prev => ({
        ...prev,
        companyName: response.data.companyName || response.data.name || '',
        logoUrl: response.data.logoUrl || '',
        industry: response.data.industry || 'retail',
        timezone: response.data.timezone || 'Asia/Kolkata',
        currency: response.data.currency || 'INR'
      }));
      
      // Load other settings if available
      if (response.data.notifications) {
        setNotifications(response.data.notifications);
      }
      if (response.data.dataSettings) {
        setDataSettings(response.data.dataSettings);
      }
      if (response.data.securitySettings) {
        setSecuritySettings(response.data.securitySettings);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (companyData.companyName) params.append('company_name', companyData.companyName);
      if (companyData.logoUrl) params.append('logo_url', companyData.logoUrl);
      
      await axios.put(`${BACKEND_URL}/api/super-admin/company/settings?${params}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Company profile updated successfully');
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      // Note: This would need a backend endpoint to save notification preferences
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulated
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Company Profile', icon: Building },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'data', label: 'Data & Storage', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'integrations', label: 'Integrations', icon: Zap }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin mx-auto mb-4 text-green-600" />
          <p className="text-slate-600">Loading company settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <SettingsIcon className="h-7 w-7 text-green-600" />
            <TranslatedText text="Company Settings" cacheKey="settings:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Manage your company profile, preferences, and configuration" cacheKey="settings:subtitle" />
          </p>
        </div>
        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
          {user?.role === 'superadmin' ? 'SuperAdmin' : 'Admin'}
        </Badge>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <nav className="flex space-x-1 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-green-600" />
                  Company Profile
                </CardTitle>
                <CardDescription>Update your company information and branding</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={companyData.companyName}
                        onChange={(e) => setCompanyData({ ...companyData, companyName: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                        placeholder="Enter company name"
                        data-testid="company-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Industry
                      </label>
                      <select
                        value={companyData.industry}
                        onChange={(e) => setCompanyData({ ...companyData, industry: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                      >
                        <option value="retail">Retail</option>
                        <option value="fmcg">FMCG</option>
                        <option value="fashion">Fashion & Apparel</option>
                        <option value="electronics">Electronics</option>
                        <option value="grocery">Grocery</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Company Logo URL
                    </label>
                    <input
                      type="url"
                      value={companyData.logoUrl}
                      onChange={(e) => setCompanyData({ ...companyData, logoUrl: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                  
                  {companyData.logoUrl && (
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Logo Preview</p>
                      <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 flex items-center justify-center">
                        <img 
                          src={companyData.logoUrl} 
                          alt="Company Logo" 
                          className="max-h-20 max-w-full object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Timezone
                      </label>
                      <select
                        value={companyData.timezone}
                        onChange={(e) => setCompanyData({ ...companyData, timezone: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                      >
                        <option value="Asia/Kolkata">India (IST)</option>
                        <option value="Asia/Dubai">Dubai (GST)</option>
                        <option value="Asia/Singapore">Singapore (SGT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="America/New_York">New York (EST)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Currency
                      </label>
                      <select
                        value={companyData.currency}
                        onChange={(e) => setCompanyData({ ...companyData, currency: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                        disabled
                      >
                        <option value="INR">&#8377; INR (Indian Rupee)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Fiscal Year Start
                      </label>
                      <select
                        value={companyData.fiscalYearStart}
                        onChange={(e) => setCompanyData({ ...companyData, fiscalYearStart: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                      >
                        <option value="January">January</option>
                        <option value="April">April</option>
                        <option value="July">July</option>
                        <option value="October">October</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <Button 
                      type="submit" 
                      className="bg-green-600 hover:bg-green-700 text-white"
                      disabled={saving}
                      data-testid="save-profile-btn"
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Profile
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-green-600" />
                  Notification Preferences
                </CardTitle>
                <CardDescription>Configure how and when you receive notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {[
                  { key: 'emailAlerts', label: 'Email Alerts', desc: 'Receive critical alerts via email' },
                  { key: 'dailyDigest', label: 'Daily Digest', desc: 'Get a daily summary of all activities' },
                  { key: 'criticalAlertsOnly', label: 'Critical Alerts Only', desc: 'Only notify for high-priority issues' },
                  { key: 'agentCompletionNotify', label: 'Agent Completion', desc: 'Notify when AI agents complete analysis' },
                  { key: 'uploadNotifications', label: 'Upload Notifications', desc: 'Notify on data upload success/failure' },
                  { key: 'weeklyReportEmail', label: 'Weekly Report', desc: 'Receive weekly business summary email' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-800 dark:text-white">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications[item.key]}
                        onChange={(e) => setNotifications({ ...notifications, [item.key]: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                ))}
                
                <div className="pt-4 flex justify-end">
                  <Button 
                    onClick={handleSaveNotifications}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Notifications
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data & Storage Tab */}
          {activeTab === 'data' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-green-600" />
                  Data & Storage Settings
                </CardTitle>
                <CardDescription>Configure data retention and storage policies</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Data Retention Period (days)
                  </label>
                  <select
                    value={dataSettings.dataRetentionDays}
                    onChange={(e) => setDataSettings({ ...dataSettings, dataRetentionDays: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                  >
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>1 year</option>
                    <option value={730}>2 years</option>
                    <option value={1095}>3 years</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Historical data older than this will be archived</p>
                </div>
                
                {[
                  { key: 'autoArchive', label: 'Auto-Archive Old Data', desc: 'Automatically archive data past retention period' },
                  { key: 'anonymizeOldData', label: 'Anonymize Archived Data', desc: 'Remove PII from archived records' },
                  { key: 'enableDataExport', label: 'Enable Data Export', desc: 'Allow users to export data to Excel/CSV' }
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-800 dark:text-white">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dataSettings[item.key]}
                        onChange={(e) => setDataSettings({ ...dataSettings, [item.key]: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                ))}
                
                {/* Storage Usage — V28: Real tenant-scoped metrics */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200" data-testid="storage-usage-section">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-blue-800 dark:text-blue-200">Storage Usage</span>
                    <span className="text-sm text-blue-600" data-testid="storage-usage-text">
                      {storageLoading ? 'Loading...' : storageMetrics ? (
                        storageMetrics.quotaFormatted 
                          ? `${storageMetrics.usedFormatted} / ${storageMetrics.quotaFormatted}`
                          : storageMetrics.usedFormatted
                      ) : 'Not available'}
                    </span>
                  </div>
                  {storageMetrics && storageMetrics.percentUsed != null ? (
                    <>
                      <div className="w-full bg-blue-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${Math.min(storageMetrics.percentUsed, 100)}%` }}></div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">{storageMetrics.percentUsed}% of storage used</p>
                    </>
                  ) : (
                    <>
                      <div className="w-full bg-blue-200 rounded-full h-2.5">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '0%' }}></div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        {storageMetrics ? (storageMetrics.quotaFormatted ? `${storageMetrics.totalDocuments.toLocaleString()} documents` : 'Quota: Not Configured') : ''}
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  Security Settings
                </CardTitle>
                <CardDescription>Configure security policies for your organization</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <select
                    value={securitySettings.sessionTimeout}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-slate-800 dark:border-slate-600"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                    <option value={480}>8 hours</option>
                    <option value={1440}>24 hours</option>
                  </select>
                </div>
                
                {[
                  { key: 'enforcePasswordPolicy', label: 'Enforce Password Policy', desc: 'Require strong passwords with complexity rules' },
                  { key: 'twoFactorEnabled', label: 'Two-Factor Authentication', desc: 'Require 2FA for all users (Coming Soon)', disabled: true }
                ].map(item => (
                  <div key={item.key} className={`flex items-center justify-between p-4 rounded-lg ${item.disabled ? 'bg-slate-100 opacity-60' : 'bg-slate-50 dark:bg-slate-800'}`}>
                    <div>
                      <p className="font-medium text-slate-800 dark:text-white">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={securitySettings[item.key]}
                        onChange={(e) => setSecuritySettings({ ...securitySettings, [item.key]: e.target.checked })}
                        className="sr-only peer"
                        disabled={item.disabled}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>
                ))}
                
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">Security Audit</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        All admin actions are logged for compliance. View the audit log to track changes.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-green-600" />
                  Integrations
                </CardTitle>
                <CardDescription>Manage third-party integrations and APIs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { name: 'ERP System', status: 'connected', icon: Database, desc: 'Sync inventory and sales data' },
                  { name: 'Email Service', status: 'connected', icon: Mail, desc: 'SendGrid for notifications' },
                  { name: 'Analytics', status: 'not_connected', icon: BarChart3, desc: 'Google Analytics integration' },
                  { name: 'Payment Gateway', status: 'not_connected', icon: CreditCard, desc: 'Stripe payment processing' }
                ].map((integration, idx) => {
                  const Icon = integration.icon;
                  return (
                    <div key={idx} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 dark:text-white">{integration.name}</p>
                          <p className="text-sm text-slate-500">{integration.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={integration.status === 'connected' ? 'default' : 'outline'} className={integration.status === 'connected' ? 'bg-green-100 text-green-700' : ''}>
                          {integration.status === 'connected' ? 'Connected' : 'Not Connected'}
                        </Badge>
                        <Button variant="outline" size="sm">
                          {integration.status === 'connected' ? 'Configure' : 'Connect'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Company Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Status</span>
                  <Badge className={settings?.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {settings?.status === 'active' ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                    {settings?.status || 'Active'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Company ID</span>
                  <span className="font-mono text-sm text-slate-900 dark:text-white">
                    {settings?.companyId || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Tenant ID</span>
                  <span className="font-mono text-xs text-slate-500 truncate max-w-[120px]">
                    {settings?.tenantId || 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Statistics Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 text-slate-400 mr-2" />
                    <span className="text-slate-600 dark:text-slate-400">Total Users</span>
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {settings?.userCount || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center">
                    <Upload className="h-4 w-4 text-slate-400 mr-2" />
                    <span className="text-slate-600 dark:text-slate-400">Data Uploads</span>
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {settings?.uploadCount || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-slate-400 mr-2" />
                    <span className="text-slate-600 dark:text-slate-400">Created</span>
                  </div>
                  <span className="text-sm text-slate-900 dark:text-white">
                    {settings?.createdAt ? new Date(settings.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200">
            <CardContent className="pt-6 space-y-3">
              <h3 className="font-semibold text-green-800 dark:text-green-200">Quick Actions</h3>
              <Button variant="outline" className="w-full justify-start border-green-200 text-green-700 hover:bg-green-100" onClick={() => window.location.href = '/admin/users'}>
                <Users className="h-4 w-4 mr-2" />
                Manage Users
              </Button>
              <Button variant="outline" className="w-full justify-start border-green-200 text-green-700 hover:bg-green-100" onClick={() => window.location.href = '/admin/audit-log'}>
                <FileText className="h-4 w-4 mr-2" />
                View Audit Log
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
